import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  MEMORY_GAME_MODE_OPTIONS,
  readMemoryGamePreference,
  subscribeMemoryGamePreference,
  writeMemoryGamePreference,
} from "../utils/memoryGamePreference";
import "./MemoryGamePreferenceDropdown.css";

export default function MemoryGamePreferenceDropdown() {
  const [selectedMode, setSelectedMode] = useState(() => readMemoryGamePreference());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = useMemo(
    () =>
      MEMORY_GAME_MODE_OPTIONS.find((option) => option.value === selectedMode) ||
      MEMORY_GAME_MODE_OPTIONS[0],
    [selectedMode]
  );

  useEffect(() => subscribeMemoryGamePreference(setSelectedMode), []);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const handleSelect = (mode) => {
    writeMemoryGamePreference(mode);
    setSelectedMode(mode);
    setDropdownOpen(false);
  };

  return (
    <div
      className={`memory-game-pref-dropdown${
        dropdownOpen ? " memory-game-pref-dropdown--open" : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="memory-game-pref-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        onClick={() => setDropdownOpen((open) => !open)}
      >
        <span className="memory-game-pref-dropdown__label">{selectedOption.label}</span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="memory-game-pref-dropdown__chevron"
          aria-hidden="true"
        />
      </button>

      {dropdownOpen && (
        <ul className="memory-game-pref-dropdown__menu" role="listbox">
          {MEMORY_GAME_MODE_OPTIONS.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === selectedMode}
                className={`memory-game-pref-dropdown__option${
                  option.value === selectedMode
                    ? " memory-game-pref-dropdown__option--selected"
                    : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
